import React from 'react';
import { map } from 'lodash';
import styled from 'styled-components';

// @ts-ignore
import { Stroke, GameStatus, Tools, User } from '../types';
import { createStroke, getStrokesForRoomRef, clearStrokes } from '../firestore/stroke';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { generateWord } from '../data/words';
import { startNextDrawer, startNextRound } from '../firestore/room';

import { MyCanvas } from './MyCanvas';

// Make everything relative to height/width
const width = 300;
const height = 300;

const SketchContainer = styled.div`
    position: relative;
`;

const Mask =
    styled.div <
    { hasBackground: boolean } >
    `
    ${(props) => (props.hasBackground ? 'background-color: rgba(0, 0, 0, 0.75);' : '')}
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const MaskText = styled.div`
    color: white;
    text-align: center;
`;

const Button = styled.button`
    background-color: white;
`;

const ToolbarContainer = styled.div`
    display: flex;
`;

const ToolButton =
    styled.button <
    { active: boolean } >
    `
${(props) => (props.active ? 'background-color: blue;' : '')}
`;

export const Sketch = React.memo(
    ({
        roomId,
        drawer,
        resetDrawing,
        gameStatus,
    }: {
        roomId: string,
        drawer?: User,
        resetDrawing: boolean,
        gameStatus: GameStatus,
    }) => {
        const { currentUserId } = React.useContext(CurrentUserContext);
        const sketchPad = React.createRef<{
            draw: (stroke: Partial<Stroke>) => void,
            clear: () => void,
            setTool: (tool: Tools) => void,
            getActiveTool: () => Tools,
        }>();
        const isDrawer = currentUserId === drawer?.id;

        const handleDraw = (stroke: Omit<Stroke, 'id' | 'roomId'>) => {
            const { start, end, strokeStyle, lineWidth } = stroke;
            if (isDrawer) {
                createStroke({
                    start,
                    end,
                    strokeStyle,
                    roomId,
                    lineWidth,
                });
            }
        };

        const handleClickPencil = () => {
            if (sketchPad.current) {
                sketchPad.current.setTool(Tools.Pencil);
            }
        };

        const handleClickEraser = () => {
            if (sketchPad.current) {
                sketchPad.current.setTool(Tools.Eraser);
            }
        };
        const handleClickClear = () => {
            if (sketchPad.current) {
                sketchPad.current.clear();
                clearStrokes(roomId);
            }
        };

        React.useEffect(() => {
            // When drawer changes, clear the easel
            if (sketchPad.current && resetDrawing) {
                sketchPad.current.clear();
            }
        }, [resetDrawing]);

        React.useEffect(() => {
            const handleChildAdded = (dataSnapshot: firebase.database.DataSnapshot) => {
                // Only load data if we are not hte artist
                if (isDrawer) {
                    return;
                }
                if (!sketchPad.current) {
                    return;
                }

                const stroke: Partial<Stroke> = dataSnapshot.val();
                sketchPad.current.draw(stroke);
            };
            getStrokesForRoomRef(roomId).on('child_added', handleChildAdded);
            return () => {
                getStrokesForRoomRef(roomId).off('child_added', handleChildAdded);
            };
        }, [roomId, isDrawer]);

        React.useEffect(() => {
            // Any time you become the drawer, reset the tool to pencil
            handleClickPencil();
        }, [isDrawer]);

        console.log(sketchPad.current?.getActiveTool());

        return (
            <SketchContainer>
                <MyCanvas onDraw={handleDraw} ref={sketchPad} />
                {isDrawer && (
                    <ToolbarContainer>
                        <ToolButton
                            active={sketchPad.current?.getActiveTool() === Tools.Pencil}
                            onClick={handleClickPencil}
                        >
                            Pencil
                        </ToolButton>
                        <ToolButton
                            active={sketchPad.current?.getActiveTool() === Tools.Eraser}
                            onClick={handleClickEraser}
                        >
                            Eraser
                        </ToolButton>
                        <button onClick={handleClickClear}>Clear</button>
                    </ToolbarContainer>
                )}
                {(gameStatus === GameStatus.GameOver ||
                    gameStatus === GameStatus.WaitingToStartGame ||
                    gameStatus === GameStatus.WaitingToStartNextDrawer ||
                    (gameStatus === GameStatus.RoundInProgress && !isDrawer)) && (
                    <Mask
                        hasBackground={
                            gameStatus === GameStatus.WaitingToStartNextDrawer ||
                            gameStatus === GameStatus.WaitingToStartGame ||
                            gameStatus === GameStatus.GameOver
                        }
                    >
                        <div>
                            {gameStatus === GameStatus.GameOver ? (
                                <>
                                    <MaskText>Game over!</MaskText>
                                    <Button onClick={() => startNextRound(roomId, 1)}>
                                        Play again
                                    </Button>
                                </>
                            ) : gameStatus === GameStatus.WaitingToStartNextDrawer ? (
                                isDrawer ? (
                                    <>
                                        <MaskText>Choose your word</MaskText>
                                        {map(
                                            [generateWord(), generateWord(), generateWord()],
                                            (word, idx) => {
                                                return (
                                                    <Button
                                                        key={idx}
                                                        onClick={() =>
                                                            startNextDrawer(roomId, word)
                                                        }
                                                    >
                                                        {word}
                                                    </Button>
                                                );
                                            },
                                        )}
                                    </>
                                ) : (
                                    <MaskText>
                                        Waiting for {drawer?.name || 'player'} to start...
                                    </MaskText>
                                )
                            ) : gameStatus === GameStatus.WaitingToStartGame ? (
                                <Button onClick={() => startNextRound(roomId, 1)}>
                                    Start Game
                                </Button>
                            ) : null}
                        </div>
                    </Mask>
                )}
            </SketchContainer>
        );
    },
);
